import { useState, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Code, Smartphone, Zap, Search, Palette, Shield, Globe, BarChart3, Sun, Moon, MousePointer2, Sparkles, Languages, Link2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useTheme } from '../context/ThemeContext';
import { SEO, STRUCTURED_DATA } from '../components/SEO';

// Lazy load heavy demo components for better performance on slower devices
const LandingPageDemo = lazy(() => import('../components/ServiceDemos').then(m => ({ default: m.LandingPageDemo })));
const EcommerceDemo = lazy(() => import('../components/ServiceDemos').then(m => ({ default: m.EcommerceDemo })));
const VitrineDemo = lazy(() => import('../components/ServiceDemos').then(m => ({ default: m.VitrineDemo })));
const DashboardDemo = lazy(() => import('../components/ServiceDemos').then(m => ({ default: m.DashboardDemo })));

// Loading placeholder for demos
const DemoLoader = () => (
  <div className="w-full h-64 flex items-center justify-center bg-muted/30 rounded-xl">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const languages = [
  { 
    code: 'fr', 
    name: 'Français', 
    title: 'Votre site, dans toutes les langues',
    subtitle: 'Touchez un public international',
    description: 'Un site multilingue permet de toucher des clients partout dans le monde. Je configure la traduction automatique ou manuelle selon vos besoins.',
    cta: 'Demander un devis'
  },
  { 
    code: 'de', 
    name: 'Deutsch', 
    title: 'Ihre Website, in jeder Sprache',
    subtitle: 'Erreichen Sie ein internationales Publikum',
    description: 'Eine mehrsprachige Website ermöglicht es Ihnen, Kunden auf der ganzen Welt zu erreichen. Ich richte automatische oder manuelle Übersetzung nach Ihren Bedürfnissen ein.',
    cta: 'Angebot anfordern'
  },
  { 
    code: 'cn', 
    name: '中文', 
    title: '您的网站，支持所有语言',
    subtitle: '触达全球客户',
    description: '多语言网站让您能够触达世界各地的客户。我可以根据您的需求配置自动或手动翻译。',
    cta: '获取报价'
  },
  { 
    code: 'ru', 
    name: 'Русский', 
    title: 'Ваш сайт на всех языках',
    subtitle: 'Охватите международную аудиторию',
    description: 'Многоязычный сайт позволяет привлекать клиентов со всего мира. Я настрою автоматический или ручной перевод в соответствии с вашими потребностями.',
    cta: 'Запросить смету'
  },
];

// Flag components as simple SVG icons with rounded corners
const FlagIcon = ({ code, className = "" }) => {
  const flags = {
    fr: (
      <svg viewBox="0 0 36 24" className={className} style={{ borderRadius: '4px', overflow: 'hidden' }}>
        <clipPath id="roundedFR">
          <rect width="36" height="24" rx="3" ry="3"/>
        </clipPath>
        <g clipPath="url(#roundedFR)">
          <rect width="12" height="24" fill="#002395"/>
          <rect x="12" width="12" height="24" fill="#FFFFFF"/>
          <rect x="24" width="12" height="24" fill="#ED2939"/>
        </g>
        <rect width="36" height="24" rx="3" ry="3" fill="none" stroke="rgba(0,0,0,0.1)" strokeWidth="1"/>
      </svg>
    ),
    de: (
      <svg viewBox="0 0 36 24" className={className} style={{ borderRadius: '4px', overflow: 'hidden' }}>
        <clipPath id="roundedDE">
          <rect width="36" height="24" rx="3" ry="3"/>
        </clipPath>
        <g clipPath="url(#roundedDE)">
          <rect width="36" height="8" fill="#000000"/>
          <rect y="8" width="36" height="8" fill="#DD0000"/>
          <rect y="16" width="36" height="8" fill="#FFCE00"/>
        </g>
        <rect width="36" height="24" rx="3" ry="3" fill="none" stroke="rgba(0,0,0,0.1)" strokeWidth="1"/>
      </svg>
    ),
    cn: (
      <svg viewBox="0 0 36 24" className={className} style={{ borderRadius: '4px', overflow: 'hidden' }}>
        <clipPath id="roundedCN">
          <rect width="36" height="24" rx="3" ry="3"/>
        </clipPath>
        <g clipPath="url(#roundedCN)">
          <rect width="36" height="24" fill="#DE2910"/>
          <g fill="#FFDE00">
            <polygon points="6,4 7.2,7.8 3.2,5.6 8.8,5.6 4.8,7.8"/>
            <polygon points="11,2 11.4,3.2 10.2,2.6 12,2.6 10.6,3.2" transform="rotate(23 11 2.5)"/>
            <polygon points="13,4 13.4,5.2 12.2,4.6 14,4.6 12.6,5.2" transform="rotate(46 13 4.5)"/>
            <polygon points="13,7 13.4,8.2 12.2,7.6 14,7.6 12.6,8.2" transform="rotate(70 13 7.5)"/>
            <polygon points="11,9 11.4,10.2 10.2,9.6 12,9.6 10.6,10.2" transform="rotate(93 11 9.5)"/>
          </g>
        </g>
        <rect width="36" height="24" rx="3" ry="3" fill="none" stroke="rgba(0,0,0,0.1)" strokeWidth="1"/>
      </svg>
    ),
    ru: (
      <svg viewBox="0 0 36 24" className={className} style={{ borderRadius: '4px', overflow: 'hidden' }}>
        <clipPath id="roundedRU">
          <rect width="36" height="24" rx="3" ry="3"/>
        </clipPath>
        <g clipPath="url(#roundedRU)">
          <rect width="36" height="8" fill="#FFFFFF"/>
          <rect y="8" width="36" height="8" fill="#0039A6"/>
          <rect y="16" width="36" height="8" fill="#D52B1E"/>
        </g>
        <rect width="36" height="24" rx="3" ry="3" fill="none" stroke="rgba(0,0,0,0.1)" strokeWidth="1"/>
      </svg>
    ),
  };
  return flags[code] || null;
};

const siteTypes = [
  {
    title: 'Site Vitrine',
    description: 'Présentez votre activité avec élégance. Design sur-mesure, optimisé pour convertir vos visiteurs en clients.',
    icon: Globe,
    color: 'from-violet-500 to-purple-600',
  },
  {
    title: 'Landing Page',
    description: 'Pages de conversion ultra-efficaces pour vos campagnes marketing et lancements de produits.',
    icon: Zap,
    color: 'from-emerald-400 to-teal-500',
  },
  {
    title: 'E-commerce',
    description: 'Boutiques en ligne performantes avec paiement sécurisé et gestion des stocks intégrée.',
    icon: BarChart3,
    color: 'from-orange-400 to-red-500',
  },
  {
    title: 'Application Web',
    description: 'Dashboards et outils métier sur-mesure pour digitaliser vos processus.',
    icon: Code,
    color: 'from-blue-500 to-indigo-600',
  },
];

const features = [
  {
    title: 'Design Responsive',
    description: 'Sites parfaitement adaptés à tous les écrans : mobile, tablette et desktop.',
    icon: Smartphone,
  },
  {
    title: 'UI/UX Moderne',
    description: 'Interfaces intuitives et esthétiques qui marquent les esprits.',
    icon: Palette,
  },
  {
    title: 'Performance',
    description: 'Sites rapides et optimisés pour une expérience utilisateur fluide.',
    icon: Zap,
  },
  {
    title: 'Sécurité',
    description: 'HTTPS, protection des données et bonnes pratiques de sécurité.',
    icon: Shield,
  },
  {
    title: 'Maintenance',
    description: 'Support et mises à jour pour garder votre site performant.',
    icon: Code,
  },
  {
    title: 'Intégrations & API',
    description: 'Connexion à vos outils : paiement, CRM, analytics et services tiers.',
    icon: Link2,
  },
];

export default function HomePage() {
  const { theme, toggleTheme } = useTheme();
  const [selectedLang, setSelectedLang] = useState('fr');

  const currentLang = languages.find(l => l.code === selectedLang);

  return (
    <main data-testid="home-page">
      <SEO 
        title="Création de sites web sur-mesure à Bourg-en-Bresse"
        description="Développeur web freelance spécialisé dans la création de sites vitrines, e-commerce et applications web. Basé à Bourg-en-Bresse, j'interviens dans l'Ain, Lyon, Savoie et Haute-Savoie. Devis gratuit sous 48h."
        path="/"
        structuredData={STRUCTURED_DATA.localBusiness}
      />
      
      {/* Hero Section */}
      <section className="min-h-screen flex items-center relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#8B7BF7]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#9EFFA0]/20 rounded-full blur-3xl" />
        </div>

        <div className="container-main pt-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-up">
              <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-bold uppercase tracking-widest mb-6">
                Web Designer Freelance
              </span>
              <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight leading-none mb-6">
                Des sites web{' '}
                <span className="text-gradient">sur-mesure</span>
                <br />
                à prix accessible
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 max-w-xl">
                Je crée des sites web modernes, performants et adaptés à votre budget. 
                De la landing page au e-commerce, donnez vie à votre projet digital.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/tarifs#contact">
                  <Button data-testid="hero-cta-contact" className="btn-primary text-lg px-10 py-6">
                    Démarrer mon projet
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/tarifs">
                  <Button data-testid="hero-cta-tarifs" variant="outline" className="btn-secondary text-lg px-10 py-6">
                    Découvrir les tarifs
                  </Button>
                </Link>
              </div>
            </div>

            {/* Hero Visual */}
            <div className="relative animate-fade-up animation-delay-200 hidden lg:block">
              <div className="relative z-10">
                <div className="bg-gradient-to-br from-[#8B7BF7] to-[#9EFFA0] p-1 rounded-3xl">
                  <div className="bg-background rounded-3xl p-8">
                    <div className="flex gap-2 mb-4">
                      <div className="w-3 h-3 rounded-full bg-red-400" />
                      <div className="w-3 h-3 rounded-full bg-yellow-400" />
                      <div className="w-3 h-3 rounded-full bg-green-400" />
                    </div>
                    <div className="space-y-3">
                      <div className="h-4 bg-muted rounded w-3/4" />
                      <div className="h-4 bg-muted rounded w-1/2" />
                      <div className="h-32 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-xl mt-4" />
                      <div className="grid grid-cols-3 gap-3 mt-4">
                        <div className="h-20 bg-muted rounded-lg" />
                        <div className="h-20 bg-muted rounded-lg" />
                        <div className="h-20 bg-muted rounded-lg" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-8 -right-8 w-64 h-64 bg-[#9EFFA0]/30 rounded-full blur-2xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Theme Demo Section */}
      <section className="section-padding bg-muted/30 overflow-hidden" data-testid="theme-demo-section">
        <div className="container-main">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-bold uppercase tracking-widest mb-6">
              <Sparkles className="h-4 w-4" />
              Démo interactive
            </span>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Vous préférez un site <span className="text-gradient">clair</span> ou <span className="text-gradient">sombre</span> ?
            </h2>
            <p className="text-lg text-muted-foreground mb-12">
              Cliquez et voyez le changement en direct. Chaque site que je crée peut s'adapter aux préférences de vos utilisateurs.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
              <button
                data-testid="theme-light-btn"
                onClick={() => { if (theme === 'dark') toggleTheme(); }}
                className={`group relative w-64 h-40 rounded-3xl transition-all duration-500 overflow-hidden ${
                  theme === 'light' 
                    ? 'ring-4 ring-primary scale-105 shadow-2xl shadow-primary/20' 
                    : 'hover:scale-102 hover:shadow-xl'
                }`}
              >
                <div className="absolute inset-0 bg-white border-2 border-gray-200">
                  <div className="p-4 h-full flex flex-col">
                    <div className="flex gap-1.5 mb-3">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                      <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="h-2 bg-gray-200 rounded w-3/4" />
                      <div className="h-2 bg-gray-200 rounded w-1/2" />
                      <div className="h-8 bg-gradient-to-r from-violet-100 to-green-100 rounded-lg mt-3" />
                    </div>
                  </div>
                </div>
                <div className={`absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/10 transition-colors ${theme === 'light' ? 'bg-primary/10' : ''}`}>
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-lg transform transition-all ${theme === 'light' ? 'scale-110' : 'opacity-0 group-hover:opacity-100'}`}>
                    <Sun className="h-5 w-5 text-yellow-500" />
                    <span className="font-bold text-gray-800">Mode Clair</span>
                  </div>
                </div>
              </button>

              <div className="text-2xl font-bold text-muted-foreground">ou</div>

              <button
                data-testid="theme-dark-btn"
                onClick={() => { if (theme === 'light') toggleTheme(); }}
                className={`group relative w-64 h-40 rounded-3xl transition-all duration-500 overflow-hidden ${
                  theme === 'dark' 
                    ? 'ring-4 ring-primary scale-105 shadow-2xl shadow-primary/20' 
                    : 'hover:scale-102 hover:shadow-xl'
                }`}
              >
                <div className="absolute inset-0 bg-[#1A1A1A] border-2 border-gray-800">
                  <div className="p-4 h-full flex flex-col">
                    <div className="flex gap-1.5 mb-3">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                      <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="h-2 bg-gray-700 rounded w-3/4" />
                      <div className="h-2 bg-gray-700 rounded w-1/2" />
                      <div className="h-8 bg-gradient-to-r from-violet-900/50 to-green-900/50 rounded-lg mt-3" />
                    </div>
                  </div>
                </div>
                <div className={`absolute inset-0 flex items-center justify-center bg-white/0 group-hover:bg-white/10 transition-colors ${theme === 'dark' ? 'bg-primary/10' : ''}`}>
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full bg-gray-800 shadow-lg transform transition-all ${theme === 'dark' ? 'scale-110' : 'opacity-0 group-hover:opacity-100'}`}>
                    <Moon className="h-5 w-5 text-violet-400" />
                    <span className="font-bold text-white">Mode Sombre</span>
                  </div>
                </div>
              </button>
            </div>

            <p className="text-sm text-muted-foreground">
              <MousePointer2 className="inline h-4 w-4 mr-1" />
              Essayez : Le site entier vient de changer !
            </p>
          </div>
        </div>
      </section>

      {/* Site Types Section - Interactive Demos */}
      <section className="section-padding bg-muted/30" data-testid="site-types-section">
        <div className="container-main">
          <div className="text-center mb-16 animate-fade-up">
            <span className="text-sm uppercase tracking-widest font-bold text-primary mb-4 block">
              Services
            </span>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Quel site pour votre projet ?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Interagissez avec ces mini-démos pour découvrir ce que je peux créer pour vous.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Site Vitrine Demo (was Landing Page) */}
            <div data-testid="site-type-vitrine" className="group">
              <Suspense fallback={<DemoLoader />}>
                <LandingPageDemo />
              </Suspense>
              <div className="mt-4 text-center">
                <h3 className="text-xl font-bold mb-1">Site Vitrine</h3>
                <p className="text-sm text-muted-foreground">Présentez votre activité avec élégance</p>
              </div>
            </div>

            {/* E-commerce Demo */}
            <div data-testid="site-type-ecommerce" className="group">
              <Suspense fallback={<DemoLoader />}>
                <EcommerceDemo />
              </Suspense>
              <div className="mt-4 text-center">
                <h3 className="text-xl font-bold mb-1">E-commerce</h3>
                <p className="text-sm text-muted-foreground">Boutiques en ligne avec panier & paiement</p>
              </div>
            </div>

            {/* Site de Formation Demo (was Site Vitrine) */}
            <div data-testid="site-type-formation" className="group">
              <Suspense fallback={<DemoLoader />}>
                <VitrineDemo />
              </Suspense>
              <div className="mt-4 text-center">
                <h3 className="text-xl font-bold mb-1">Site de Formation</h3>
                <p className="text-sm text-muted-foreground">Plateformes e-learning et cours en ligne</p>
              </div>
            </div>

            {/* Dashboard Demo */}
            <div data-testid="site-type-dashboard" className="group">
              <Suspense fallback={<DemoLoader />}>
                <DashboardDemo />
              </Suspense>
              <div className="mt-4 text-center">
                <h3 className="text-xl font-bold mb-1">Application Web</h3>
                <p className="text-sm text-muted-foreground">Dashboards & outils métier sur-mesure</p>
              </div>
            </div>
          </div>

          <p className="text-center text-sm text-muted-foreground mt-12">
            <MousePointer2 className="inline h-4 w-4 mr-1" />
            Toutes ces démos sont interactives - cliquez, survolez, testez !
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding" data-testid="features-section">
        <div className="container-main">
          <div className="text-center mb-16 animate-fade-up">
            <span className="text-sm uppercase tracking-widest font-bold text-primary mb-4 block">
              Fonctionnalités
            </span>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Ce que j'apporte à chaque projet
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Des standards de qualité élevés pour des sites qui performent.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                data-testid={`feature-${index}`}
                className="group relative p-8 rounded-2xl bg-muted/50 hover:bg-primary/5 transition-all duration-500 border border-transparent hover:border-primary/20 cursor-pointer overflow-hidden"
              >
                {/* Animated background on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
                    <feature.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors">{feature.title}</h3>
                  <p className="text-muted-foreground group-hover:text-foreground transition-colors">{feature.description}</p>
                </div>
                
                {/* Corner accent */}
                <div className="absolute -bottom-2 -right-2 w-16 h-16 bg-primary/10 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:scale-150" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Section with Interactive Illustration */}
      <section className="section-padding overflow-hidden" data-testid="seo-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Interactive Illustration */}
            <div className="relative h-[400px] flex items-center justify-center order-2 lg:order-1">
              
              {/* Speed Lines Container - Continuous trail under the #1 card */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
                {/* Continuous flow of lines - spread across full card width */}
                <div className="absolute rounded-full animate-speed-line" style={{ left: '20%', width: '4px', height: '100px', background: 'linear-gradient(to bottom, hsl(var(--primary) / 0.6), transparent)', top: '38%', animationDelay: '600ms' }} />
                <div className="absolute rounded-full animate-speed-line" style={{ left: '25%', width: '2px', height: '70px', background: 'linear-gradient(to bottom, hsl(var(--primary) / 0.5), transparent)', top: '38%', animationDelay: '1300ms' }} />
                <div className="absolute rounded-full animate-speed-line" style={{ left: '30%', width: '4px', height: '100px', background: 'linear-gradient(to bottom, hsl(var(--primary) / 0.7), transparent)', top: '38%', animationDelay: '0ms' }} />
                <div className="absolute rounded-full animate-speed-line" style={{ left: '35%', width: '2px', height: '70px', background: 'linear-gradient(to bottom, hsl(var(--primary) / 0.5), transparent)', top: '38%', animationDelay: '800ms' }} />
                <div className="absolute rounded-full animate-speed-line" style={{ left: '40%', width: '2px', height: '70px', background: 'linear-gradient(to bottom, hsl(var(--primary) / 0.6), transparent)', top: '38%', animationDelay: '300ms' }} />
                <div className="absolute rounded-full animate-speed-line" style={{ left: '45%', width: '4px', height: '100px', background: 'linear-gradient(to bottom, hsl(var(--primary) / 0.75), transparent)', top: '38%', animationDelay: '1200ms' }} />
                <div className="absolute rounded-full animate-speed-line" style={{ left: '50%', width: '2px', height: '70px', background: 'linear-gradient(to bottom, hsl(var(--primary) / 0.5), transparent)', top: '38%', animationDelay: '500ms' }} />
                <div className="absolute rounded-full animate-speed-line" style={{ left: '55%', width: '4px', height: '100px', background: 'linear-gradient(to bottom, hsl(var(--primary) / 0.8), transparent)', top: '38%', animationDelay: '1500ms' }} />
                <div className="absolute rounded-full animate-speed-line" style={{ left: '60%', width: '2px', height: '70px', background: 'linear-gradient(to bottom, hsl(var(--primary) / 0.6), transparent)', top: '38%', animationDelay: '200ms' }} />
                <div className="absolute rounded-full animate-speed-line" style={{ left: '65%', width: '4px', height: '100px', background: 'linear-gradient(to bottom, hsl(var(--primary) / 0.7), transparent)', top: '38%', animationDelay: '900ms' }} />
                <div className="absolute rounded-full animate-speed-line" style={{ left: '70%', width: '2px', height: '70px', background: 'linear-gradient(to bottom, hsl(var(--primary) / 0.55), transparent)', top: '38%', animationDelay: '1100ms' }} />
                <div className="absolute rounded-full animate-speed-line" style={{ left: '75%', width: '2px', height: '70px', background: 'linear-gradient(to bottom, hsl(var(--primary) / 0.5), transparent)', top: '38%', animationDelay: '400ms' }} />
                <div className="absolute rounded-full animate-speed-line" style={{ left: '80%', width: '4px', height: '100px', background: 'linear-gradient(to bottom, hsl(var(--primary) / 0.75), transparent)', top: '38%', animationDelay: '700ms' }} />
                <div className="absolute rounded-full animate-speed-line" style={{ left: '85%', width: '2px', height: '70px', background: 'linear-gradient(to bottom, hsl(var(--primary) / 0.6), transparent)', top: '38%', animationDelay: '1400ms' }} />
              </div>

              {/* Google Search Bar - z-10 above lines */}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 w-full max-w-md z-10">
                <div className="bg-card border-2 border-border rounded-full px-6 py-3 flex items-center gap-3 shadow-lg">
                  <Search className="h-5 w-5 text-muted-foreground" />
                  <span className="text-muted-foreground text-sm">votre entreprise</span>
                </div>
              </div>

              {/* Search Results - z-10 above lines */}
              <div className="absolute top-20 left-1/2 -translate-x-1/2 w-full max-w-md space-y-3 z-10">
                {/* Result 1 - Your site going up */}
                <div className="relative">
                  <div className="bg-gradient-to-r from-primary/20 to-secondary/20 border-2 border-primary rounded-xl p-4 shadow-xl animate-bounce-slow relative bg-card/95 backdrop-blur-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-4 h-4 rounded bg-primary/30" />
                      <span className="text-xs text-primary font-bold">votresite.fr</span>
                    </div>
                    <div className="text-sm font-bold text-foreground mb-1">Votre Entreprise - Site Officiel</div>
                    <div className="text-xs text-muted-foreground">Le meilleur service de la région. Découvrez nos offres...</div>

                    {/* Rocket icon */}
                    <div className="absolute -top-4 -right-4">
                      <div className="relative">
                        <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white text-lg shadow-lg shadow-primary/30">
                          🚀
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Position badge */}
                  <div className="absolute -left-4 top-1/2 -translate-y-1/2 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-black text-sm shadow-lg shadow-primary/30">
                    1
                  </div>
                </div>

                {/* Result 2 - Competitor */}
                <div className="bg-card border border-border rounded-xl p-4 opacity-60">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-4 h-4 rounded bg-muted" />
                    <span className="text-xs text-muted-foreground">concurrent.fr</span>
                  </div>
                  <div className="h-3 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-2 bg-muted rounded w-full" />
                </div>

                {/* Result 3 - Another competitor */}
                <div className="bg-card border border-border rounded-xl p-4 opacity-40">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-4 h-4 rounded bg-muted" />
                    <span className="text-xs text-muted-foreground">autre-site.fr</span>
                  </div>
                  <div className="h-3 bg-muted rounded w-2/3 mb-2" />
                  <div className="h-2 bg-muted rounded w-full" />
                </div>
              </div>

              {/* Decorative glow elements */}
              <div className="absolute bottom-0 left-1/4 w-48 h-48 bg-primary/10 rounded-full blur-3xl z-0" />
              <div className="absolute top-1/3 right-1/4 w-32 h-32 bg-secondary/10 rounded-full blur-2xl z-0" />
            </div>

            {/* Right - Text Content */}
            <div className="order-1 lg:order-2">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-bold uppercase tracking-widest mb-6">
                <Search className="h-4 w-4" />
                SEO & Référencement
              </span>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
                En première page <span className="text-gradient">Google</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                Un beau site ne sert à rien si personne ne le trouve. Je structure chaque site pour qu'il soit 
                facilement indexé par Google et qu'il remonte dans les résultats de recherche.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Structure HTML sémantique optimisée',
                  'Meta tags et Open Graph configurés',
                  'Performance et temps de chargement rapide',
                  'Mobile-first pour le ranking Google',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-primary" />
                    </div>
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/tarifs#contact">
                <Button className="btn-primary">
                  Optimiser mon site
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Multilingual Demo Section */}
      <section className="section-padding bg-muted/30" data-testid="multilingual-demo-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Info */}
            <div className="animate-fade-up">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-bold uppercase tracking-widest mb-6">
                <Languages className="h-4 w-4" />
                Sites Multilingues
              </span>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
                Vous travaillez à l'<span className="text-gradient">international</span> ?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Cliquez sur un drapeau et voyez le contenu changer instantanément. 
                Vos clients naviguent dans leur langue, vous gagnez en crédibilité.
              </p>

              {/* Language Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-background border border-border">
                  <div className="text-3xl font-black text-primary mb-1">+40%</div>
                  <div className="text-sm text-muted-foreground">de conversions avec un site multilingue</div>
                </div>
                <div className="p-4 rounded-xl bg-background border border-border">
                  <div className="text-3xl font-black text-primary mb-1">75%</div>
                  <div className="text-sm text-muted-foreground">des clients préfèrent acheter dans leur langue</div>
                </div>
              </div>
            </div>

            {/* Right - Interactive Demo */}
            <div className="animate-fade-up animation-delay-200">
              <Card className="border-2 overflow-hidden">
                {/* Language Selector - Responsive */}
                <div className="p-4 bg-muted/50 border-b border-border">
                  <p className="text-sm text-muted-foreground text-center mb-3">Choisissez une langue :</p>
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        data-testid={`lang-btn-${lang.code}`}
                        onClick={() => setSelectedLang(lang.code)}
                        className={`relative group flex items-center gap-2 px-3 py-2 rounded-full transition-all duration-300 ${
                          selectedLang === lang.code
                            ? 'bg-primary text-primary-foreground scale-105 shadow-lg shadow-primary/20'
                            : 'bg-background hover:bg-primary/10 border border-border hover:border-primary/50'
                        }`}
                      >
                        <FlagIcon code={lang.code} className="w-6 h-4" />
                        <span className={`text-sm font-medium ${
                          selectedLang === lang.code ? '' : 'text-muted-foreground group-hover:text-foreground'
                        }`}>
                          {lang.code.toUpperCase()}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Dynamic Content */}
                <CardContent className="p-8">
                  <div 
                    key={selectedLang} 
                    className="animate-fade-up"
                  >
                    {/* Flag Badge */}
                    <div className="flex items-center gap-4 mb-6">
                      <FlagIcon code={selectedLang} className="w-16 h-10 shadow-md" />
                      <div>
                        <div className="text-sm text-primary font-bold uppercase tracking-widest">
                          {currentLang.name}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Contenu traduit automatiquement
                        </div>
                      </div>
                    </div>

                    {/* Translated Content */}
                    <h3 className="text-2xl md:text-3xl font-bold mb-2 transition-all">
                      {currentLang.title}
                    </h3>
                    <p className="text-primary font-medium mb-4">
                      {currentLang.subtitle}
                    </p>
                    <p className="text-muted-foreground mb-6 leading-relaxed">
                      {currentLang.description}
                    </p>

                    <Link to="/tarifs#contact">
                      <Button className="btn-primary">
                        {currentLang.cta}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>

              <p className="text-center text-sm text-muted-foreground mt-4">
                <MousePointer2 className="inline h-4 w-4 mr-1" />
                Le contenu change en temps réel !
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding" data-testid="cta-section">
        <div className="container-main">
          <div className="relative rounded-3xl bg-gradient-to-br from-[#8B7BF7] to-[#9EFFA0] p-1">
            <div className="bg-background rounded-3xl px-4 py-8 md:p-20 text-center">
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6 text-center">
                Prêt à lancer votre projet ?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8 text-center">
                Discutons de votre projet et trouvons ensemble la meilleure solution pour votre présence en ligne.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center px-4">
                <Link to="/tarifs#contact" className="w-full sm:w-auto">
                  <Button data-testid="cta-final-contact" className="btn-primary text-base sm:text-lg px-6 sm:px-10 py-5 sm:py-6 w-full">
                    Demander un devis gratuit
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/tarifs" className="w-full sm:w-auto">
                  <Button data-testid="cta-final-tarifs" variant="outline" className="btn-secondary text-base sm:text-lg px-6 sm:px-10 py-5 sm:py-6 w-full">
                    Voir les tarifs
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
